/*
 * Formatting, mostly of lap times.
 *
 * Lap times always keep three decimals and never lose a trailing zero: a
 * column reading 44.076 / 44.08 / 44.1 is unscannable, and in karting the
 * thousandth is a real margin rather than noise.
 */

export function lapTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) return '—'
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60)
    const s = seconds - m * 60
    return `${m}:${s.toFixed(3).padStart(6, '0')}`
  }
  return `${seconds.toFixed(3)}s`
}

export function delta(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) return '—'
  return `${seconds >= 0 ? '+' : ''}${seconds.toFixed(3)}s`
}

/** Video seek position, e.g. 542.28 -> "09:02.28". */
export function clock(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds - m * 60
  return `${String(m).padStart(2, '0')}:${s.toFixed(2).padStart(5, '0')}`
}

/*
 * Session times are always rendered in the TRACK's timezone, never the
 * viewer's.
 *
 * A session happened at a time at the track. Formatted against the browser,
 * Saturday's 12:45 final at Toowoomba reads as 03:45 to someone watching from
 * Europe - and being able to watch from anywhere is the entire point of this
 * being hosted. The API sends the zone alongside each session; when it is
 * absent the browser's own zone is the only honest fallback.
 */

const DEFAULT_TZ = 'Australia/Brisbane'

function parts(iso: string, tz: string | undefined) {
  const d = new Date(iso)
  const fmt = new Intl.DateTimeFormat('en-AU', {
    timeZone: tz || DEFAULT_TZ,
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
  const out: Record<string, string> = {}
  for (const p of fmt.formatToParts(d)) out[p.type] = p.value
  return out
}

export function shortDate(iso: string | null | undefined,
                          tz?: string): string {
  if (!iso) return '—'
  const p = parts(iso, tz)
  return `${p.month} ${p.day}`
}

export function timeOfDay(iso: string | null | undefined,
                          tz?: string): string {
  if (!iso) return ''
  const p = parts(iso, tz)
  return `${p.hour}:${p.minute}`
}

export function dateTime(iso: string | null | undefined,
                         tz?: string): string {
  if (!iso) return '—'
  const p = parts(iso, tz)
  return `${p.month} ${p.day}, ${p.year} ${p.hour}:${p.minute}`
}

/**
 * An event's date span, written the way a race calendar writes it.
 *
 * "15-16 August 2026", not "Aug 15 - Aug 16" - the month and year are said
 * once, because a meeting is one thing that happened over some days rather
 * than two dates that happen to be near each other.
 */
export function eventDates(startIso: string | null | undefined,
                           endIso: string | null | undefined,
                           tz?: string): string {
  if (!startIso) return '—'
  const s = parts(startIso, tz)
  if (!endIso) return `${s.day} ${s.month} ${s.year}`
  const e = parts(endIso, tz)
  if (s.day === e.day && s.month === e.month && s.year === e.year) {
    return `${s.day} ${s.month} ${s.year}`
  }
  if (s.month === e.month && s.year === e.year) {
    return `${s.day}–${e.day} ${s.month} ${s.year}`
  }
  if (s.year === e.year) {
    return `${s.day} ${s.month} – ${e.day} ${e.month} ${s.year}`
  }
  return `${s.day} ${s.month} ${s.year} – ${e.day} ${e.month} ${e.year}`
}

export function longDate(d: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                'Friday', 'Saturday']
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December']
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

/** "Toowoomba" or "Toowoomba (Reverse)" - never merged, they are different
 *  circuits and a lap from one says nothing about the other. */
export function layoutName(
  track: { name: string } | null,
  layout: { name: string; direction: string } | null,
): string {
  if (!track) return 'Unknown track'
  if (!layout || layout.name === 'Main') return track.name
  return `${track.name} (${layout.name})`
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]!.toUpperCase()).join('') || '?'
}
